/**
 * The title theme: an ORIGINAL sixteen-bar march in D minor at 104 bpm - a dotted, rising call
 * over a pulsing bass and a field snare, answered by an eight-bar lift through the relative
 * major and home by way of the dominant. It loops. Its first two and last two bars are the
 * touchdown reprise (01 §10: the original reprised its one tune as boots hit the ground).
 */

import { type Song, track } from './pattern';

/** The melody, bar by bar. Chords: Dm Dm Bb C | Dm Dm Bb A | F C Dm Bb | Gm Dm/A A Dm. */
export const TITLE_MELODY: readonly string[] = [
  'D4 - - D4 D4 - F4 - A4 - - - - - A4 G4',
  'F4 - - - D4 - - - . . . . D4 - E4 -',
  'F4 - - F4 F4 - G4 - Bb4 - - - - - Bb4 A4',
  'G4 - - - E4 - - - . . . . E4 - F4 -',
  'A4 - - A4 A4 - C5 - D5 - - - - - D5 C5',
  'A4 - - - F4 - - - . . . . F4 - G4 -',
  'Bb4 - - - A4 - G4 - F4 - - - E4 - D4 -',
  'C#4 - - - E4 - - - A4 - - - - - - -',
  'C5^ - - - - - A4 - F4 - - - A4 - - -',
  'G4 - - - - - E4 - C4 - - - E4 - - -',
  'F4 - - - - - A4 - D5 - - - - - C5 -',
  'Bb4 - - - - - - - . . A4 - Bb4 - A4 -',
  'G4 - - G4 G4 - Bb4 - D5 - - - - - D5 C5',
  'A4 - - - F4 - - - D4 - - - F4 - A4 -',
  'E4 - - E4 E4 - A4 - C#5 - - - - - - -',
  'D5^ - - - A4 - - - D4 - - - . . . .',
];

/** Eighth-note bass pulse: root, root, root, octave, root, root, fifth, octave. */
const pulse = (root: string, fifth: string, octave: string): string =>
  `${root} . ${root} . ${root} . ${octave} . ${root} . ${root} . ${fifth} . ${octave} .`;

const BASS = {
  Dm: pulse('D2', 'A2', 'D3'),
  Bb: pulse('Bb1', 'F2', 'Bb2'),
  C: pulse('C2', 'G2', 'C3'),
  A: pulse('A1', 'E2', 'A2'),
  F: pulse('F2', 'C3', 'F3'),
  Gm: pulse('G1', 'D2', 'G2'),
  DmA: pulse('A1', 'D2', 'A2'),
} as const;

export const TITLE_BASS: readonly string[] = [
  BASS.Dm, BASS.Dm, BASS.Bb, BASS.C, BASS.Dm, BASS.Dm, BASS.Bb, BASS.A,
  BASS.F, BASS.C, BASS.Dm, BASS.Bb, BASS.Gm, BASS.DmA, BASS.A, BASS.Dm,
];

/** Broken chords in the gap between bass and tune. */
const broken = (a: string, b: string, c: string): string => `${a} . ${b} . ${c} . ${b} . ${a} . ${b} . ${c} . ${b} .`;

const ARP = {
  Dm: broken('D3', 'F3', 'A3'),
  Bb: broken('Bb2', 'D3', 'F3'),
  C: broken('C3', 'E3', 'G3'),
  A: broken('A2', 'C#3', 'E3'),
  F: broken('F3', 'A3', 'C4'),
  Gm: broken('G2', 'Bb2', 'D3'),
  DmA: broken('A2', 'D3', 'F3'),
} as const;

export const TITLE_ARP: readonly string[] = [
  ARP.Dm, ARP.Dm, ARP.Bb, ARP.C, ARP.Dm, ARP.Dm, ARP.Bb, ARP.A,
  ARP.F, ARP.C, ARP.Dm, ARP.Bb, ARP.Gm, ARP.DmA, ARP.A, ARP.Dm,
];

const MARCH = 'o . x x X . . x o . x x X . x x';
const MARCH_FILL = 'X . x x X . x x o o o o x x X X';
const KICK = 'X . . . . . . . X . . . . . x .';

export const TITLE_SNARE: readonly string[] = [
  MARCH, MARCH, MARCH, MARCH, MARCH, MARCH, MARCH, MARCH_FILL,
  MARCH, MARCH, MARCH, MARCH, MARCH, MARCH, MARCH, MARCH_FILL,
];
export const TITLE_KICK: readonly string[] = TITLE_SNARE.map(() => KICK);

export const TITLE_BPM = 104;

export const TITLE_THEME: Song = {
  name: 'title',
  bpm: TITLE_BPM,
  steps: TITLE_MELODY.length * 16,
  tracks: [
    track('melody', 'lead', 'always', 0.9, TITLE_MELODY),
    track('bass', 'bass', 'always', 0.8, TITLE_BASS),
    track('arp', 'arp', 'always', 0.7, TITLE_ARP),
    track('snare', 'snare', 'always', 0.75, TITLE_SNARE),
    track('kick', 'kick', 'always', 0.7, TITLE_KICK),
  ],
};

/** Bars 1, 2, 15 and 16: the call, and straight to the cadence. About nine seconds. */
const REPRISE = [0, 1, 14, 15] as const;
const pickBars = (bars: readonly string[]): string[] => REPRISE.map((i) => bars[i] ?? '');

export const TOUCHDOWN_REPRISE: Song = {
  name: 'touchdown',
  bpm: TITLE_BPM,
  steps: REPRISE.length * 16,
  tracks: [
    track('melody', 'lead', 'always', 0.9, pickBars(TITLE_MELODY)),
    track('bass', 'bass', 'always', 0.8, pickBars(TITLE_BASS)),
    track('snare', 'snare', 'always', 0.75, [MARCH, MARCH, MARCH_FILL, 'X . x x X . x x X . . . . . . .']),
    track('kick', 'kick', 'always', 0.7, [KICK, KICK, KICK, KICK]),
  ],
};
